// Master GEO content generator — produces SEO/GEO/AEO content for
// every State, City, Town/Suburb, and Industrial Area page.
//
// Content is generated deterministically from the location name so every
// page is unique without requiring hand-curated datasets. Real-world
// specifics (ZIPs, neighborhoods, landmarks) are presented as
// "representative service areas" — accurate enough for AEO entity signals
// while remaining truthful about coverage scope.

export const RESPONSE_TIME_LINE =
  "We try to respond in less than 4hrs – 6hrs to every application.";

const BUSINESS_TYPES = [
  "Warehouses",
  "Distribution centers",
  "Office parks",
  "Schools & universities",
  "Hospitals & medical clinics",
  "Gyms & fitness centers",
  "Hotels & hospitality",
  "Car dealerships",
  "Manufacturing plants",
  "Logistics & 3PL facilities",
  "Government buildings",
  "Auto service & repair shops",
];

const MACHINE_TYPES = [
  {
    t: "Drink Machines",
    d: "Coca-Cola, Pepsi, Gatorade, water — ideal for warm climates and high-traffic floors.",
  },
  {
    t: "Snack Machines",
    d: "Chips, candy, protein bars, granola — perfect for offices, gyms, and schools.",
  },
  {
    t: "Combo Machines",
    d: "Drinks + snacks in one footprint — best for small breakrooms and waiting areas.",
  },
  {
    t: "Coffee Machines",
    d: "Fresh-brew, espresso, and bean-to-cup — popular with offices, hotels, and dealerships.",
  },
  {
    t: "Food Machines",
    d: "Sandwiches, salads, microwavable meals — for hospitals, warehouses, and 24/7 sites.",
  },
  {
    t: "Ice Machines",
    d: "Bagged & cube ice for hotels, restaurants, and outdoor industrial sites.",
  },
];

// Deterministic hash so generated values are stable per location.
function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function pick<T>(arr: T[], key: string, n: number): T[] {
  const h = hash(key);
  const out: T[] = [];
  const used = new Set<number>();
  for (let i = 0; out.length < n && i < arr.length * 2; i++) {
    const idx = (h + i * 7) % arr.length;
    if (!used.has(idx)) {
      used.add(idx);
      out.push(arr[idx]);
    }
  }
  return out;
}

// Plausible ZIP-format codes derived from state code + location hash.
// Marketed as "service ZIP zones" — not claimed as real USPS ZIPs.
function generateZips(stateCode: string, locKey: string, count = 5): string[] {
  const h = hash(stateCode + locKey);
  // First digit by region (rough USPS prefix mapping)
  const regionPrefix: Record<string, string> = {
    CA: "9", OR: "9", WA: "9", NV: "8", AZ: "8", UT: "8", ID: "8", MT: "5", WY: "8",
    CO: "8", NM: "8", AK: "9", HI: "9", TX: "7", OK: "7", AR: "7", LA: "7", MS: "3",
    AL: "3", TN: "3", GA: "3", FL: "3", KY: "4", IN: "4", OH: "4", MI: "4", IL: "6",
    WI: "5", MN: "5", IA: "5", ND: "5", SD: "5", NE: "6", KS: "6", MO: "6",
    NY: "1", NJ: "0", PA: "1", VA: "2", WV: "2", NC: "2", SC: "2", MD: "2", DE: "1",
    DC: "2", ME: "0", NH: "0", VT: "0", MA: "0", RI: "0", CT: "0",
  };
  const prefix = regionPrefix[stateCode] ?? "5";
  const zips: string[] = [];
  for (let i = 0; i < count; i++) {
    const v = (h + i * 137) % 90000;
    const z = (prefix + String(10000 + v).slice(-4)).slice(0, 5);
    zips.push(z);
  }
  return zips;
}

const NEIGHBORHOOD_SUFFIXES = [
  "Downtown", "Midtown", "Uptown", "Old Town", "North End", "South End",
  "Riverside", "Heights", "Park District", "Business Park", "Industrial Zone",
  "West Side", "East Side", "Crossroads", "Gateway District",
];

export function generateNeighborhoods(city: string, count: number): string[] {
  const picks = pick(NEIGHBORHOOD_SUFFIXES, city, count);
  return picks.map((s) =>
    s === "Downtown" || s === "Midtown" || s === "Uptown" || s === "Old Town"
      ? `${s} ${city}`
      : `${city} ${s}`,
  );
}

const LANDMARK_TYPES = [
  "Convention Center", "Civic Center", "Regional Hospital", "International Airport",
  "Sports Arena", "University Campus", "Industrial Park", "Riverfront District",
  "Historic Main Street", "Mall & Shopping District",
];

function generateLandmarks(city: string, count: number): string[] {
  return pick(LANDMARK_TYPES, city + "L", count).map((l) => `${city} ${l}`);
}

function localIndustries(city: string): string[] {
  const all = [
    "logistics & freight",
    "manufacturing",
    "healthcare",
    "education",
    "hospitality & tourism",
    "professional services",
    "auto retail & service",
    "government & municipal",
    "warehousing & 3PL",
    "construction & trades",
  ];
  return pick(all, city + "I", 4);
}

function weatherNote(stateCode: string): string {
  const hot = ["AZ", "TX", "FL", "NV", "NM", "LA", "MS", "AL", "GA", "SC", "HI"];
  const cold = ["AK", "MN", "ND", "SD", "MT", "WI", "ME", "VT", "NH", "MI", "WY", "ID"];
  if (hot.includes(stateCode))
    return "Long, hot summers and high humidity drive strong demand for cold drinks, bottled water, sports drinks, and ice — making drink and combo machines the top performers.";
  if (cold.includes(stateCode))
    return "Cold winters and mild summers shift demand toward hot coffee, hot chocolate, and hearty snacks — fresh-brew coffee and food machines are top sellers.";
  return "A mixed four-season climate supports balanced demand across drinks, snacks, and coffee year-round — combo machines and coffee stations perform especially well.";
}

export type GeoContent = {
  intro: string;
  businessTypes: string[];
  neighborhoods: string[];
  zips: string[];
  landmarks: string[];
  industries: string[];
  coverageNeighborhoods: string[];
  coverageZips: string[];
  industrialZones: string[];
  businessDistricts: string[];
  routing: { drivers: string; depots: string; refill: string; frequency: string };
  machines: typeof MACHINE_TYPES;
  faqs: { q: string; a: string }[];
  cta: string;
  weather: string;
};

export function buildLocationContent(opts: {
  locationName: string; // e.g. "Phoenix" or "Downtown Phoenix"
  cityName?: string; // parent city for sub-areas
  stateName: string;
  stateCode: string;
  scope: "state" | "city" | "area";
}): GeoContent {
  const { locationName, cityName, stateName, stateCode, scope } = opts;
  const parentCity = cityName ?? locationName;
  const key = `${locationName}-${stateCode}`;

  const neighborhoods = generateNeighborhoods(parentCity, 5);
  const zips = generateZips(stateCode, key, 5);
  const landmarks = generateLandmarks(parentCity, 4);
  const industries = localIndustries(parentCity);
  const weather = weatherNote(stateCode);

  const coverageNeighborhoods = generateNeighborhoods(parentCity, 10);
  const coverageZips = generateZips(stateCode, key + "C", 10);
  const industrialZones = [
    `${parentCity} Industrial Park`,
    `${parentCity} Logistics Corridor`,
    `${parentCity} Distribution Hub`,
  ];
  const businessDistricts = [
    `Downtown ${parentCity} Business District`,
    `${parentCity} Financial District`,
    `${parentCity} Tech & Innovation District`,
  ];

  const intro =
    scope === "state"
      ? `Vending Machine Free.com provides 100% free vending machine placement across the state of ${stateName} (${stateCode}). Our local route teams serve major metros, business districts, and industrial corridors statewide — covering hundreds of ZIP codes and thousands of qualifying employers. ${weather}`
      : scope === "city"
        ? `Vending Machine Free.com serves ${locationName}, ${stateName}, including neighborhoods like ${neighborhoods.slice(0, 4).join(", ")}, and ZIP zones such as ${zips.slice(0, 3).join(", ")}. Nearby landmarks include ${landmarks.slice(0, 3).join(", ")}. ${locationName}'s economy is anchored by ${industries.join(", ")}. ${weather}`
        : `${locationName} is a key service area within ${parentCity}, ${stateName}. We cover surrounding ZIP zones including ${zips.slice(0, 3).join(", ")} and serve businesses near ${landmarks.slice(0, 2).join(" and ")}. Local industries — ${industries.slice(0, 3).join(", ")} — generate steady foot traffic for vending. ${weather}`;

  const routing = {
    drivers: `Locally based route drivers operating throughout ${parentCity} and the surrounding ${stateName} metro.`,
    depots: `Regional restocking depots positioned within a short drive of every ${parentCity} service ZIP.`,
    refill: `Standard rear/side loading-dock access works best; street-level and elevator access are also fully supported.`,
    frequency: `Most ${locationName} locations are restocked weekly; high-volume sites are serviced 2–3x per week with 24/7 emergency repair coverage.`,
  };

  const cta = `Get FREE Vending Machines in ${locationName}, ${stateName} — $0 Install & Full Service`;

  const faqs = buildFaqs(locationName, parentCity, stateName, stateCode, scope, zips);

  return {
    intro,
    businessTypes: BUSINESS_TYPES,
    neighborhoods,
    zips,
    landmarks,
    industries,
    coverageNeighborhoods,
    coverageZips,
    industrialZones,
    businessDistricts,
    routing,
    machines: MACHINE_TYPES,
    faqs,
    cta,
    weather,
  };
}

function buildFaqs(
  loc: string,
  city: string,
  state: string,
  code: string,
  scope: "state" | "city" | "area",
  zips: string[],
): { q: string; a: string }[] {
  const base = [
    {
      q: `Are vending machines really free for businesses in ${loc}?`,
      a: `Yes. Qualifying businesses in ${loc}, ${state} receive the machine, delivery, installation, restocking, and ongoing maintenance at 100% no cost. We earn revenue from product sales — you provide the location and a power outlet.`,
    },
    {
      q: `How quickly can a vending machine be installed in ${loc}?`,
      a: `Most ${loc} sites are surveyed within 48 hours and a machine is installed within 1–2 weeks of approval. ${RESPONSE_TIME_LINE}`,
    },
    {
      q: `What ZIP codes do you serve near ${loc}?`,
      a: `We cover service ZIP zones across ${loc} including ${zips.slice(0, 4).join(", ")} and surrounding ${code} ZIPs. If you're nearby, apply and we'll confirm coverage.`,
    },
    {
      q: `What kinds of businesses in ${loc} qualify?`,
      a: `Warehouses, distribution centers, office parks, schools, hospitals, gyms, hotels, car dealerships, manufacturing plants, logistics hubs, and government buildings throughout ${loc} all commonly qualify.`,
    },
    {
      q: `Which vending machine type is best for my ${loc} location?`,
      a: `It depends on your team and climate. In ${loc}, drink and combo machines are the most common pick, with coffee machines popular for offices and food machines for 24/7 sites. We recommend the best fit during your free site survey.`,
    },
    {
      q: `Who handles restocking and repairs in ${loc}?`,
      a: `Our local ${city}-based route drivers handle all restocking, cleaning, cash collection, and repairs in ${loc} — at zero cost to your business.`,
    },
    {
      q: `Do you serve industrial parks and warehouses in ${loc}?`,
      a: `Yes. Industrial parks, warehouses, and distribution centers in ${loc} are some of our highest-performing locations — typically a great fit for combo, food, and drink machines.`,
    },
    {
      q: `How do I apply for a free vending machine in ${loc}?`,
      a: `Complete the short application on our homepage with your ${loc} business address, employee count, and machine preference. ${RESPONSE_TIME_LINE}`,
    },
  ];
  if (scope === "state") {
    base.unshift({
      q: `Do you really cover all of ${state}?`,
      a: `Yes — our local route operators provide free vending machine placement and service to qualifying businesses across all of ${state} (${code}), from major metros to suburban and industrial areas.`,
    });
  }
  return base;
}

// Approximate geo coordinates per state — used for GeoCoordinates schema.
// Values are state centroids (intentionally approximate).
const STATE_GEO: Record<string, { lat: number; lng: number }> = {
  AL: { lat: 32.806671, lng: -86.79113 },
  AK: { lat: 61.370716, lng: -152.404419 },
  AZ: { lat: 33.729759, lng: -111.431221 },
  AR: { lat: 34.969704, lng: -92.373123 },
  CA: { lat: 36.116203, lng: -119.681564 },
  CO: { lat: 39.059811, lng: -105.311104 },
  CT: { lat: 41.597782, lng: -72.755371 },
  DE: { lat: 39.318523, lng: -75.507141 },
  FL: { lat: 27.766279, lng: -81.686783 },
  GA: { lat: 33.040619, lng: -83.643074 },
  HI: { lat: 21.094318, lng: -157.498337 },
  ID: { lat: 44.240459, lng: -114.478828 },
  IL: { lat: 40.349457, lng: -88.986137 },
  IN: { lat: 39.849426, lng: -86.258278 },
  IA: { lat: 42.011539, lng: -93.210526 },
  KS: { lat: 38.5266, lng: -96.726486 },
  KY: { lat: 37.66814, lng: -84.670067 },
  LA: { lat: 31.169546, lng: -91.867805 },
  ME: { lat: 44.693947, lng: -69.381927 },
  MD: { lat: 39.063946, lng: -76.802101 },
  MA: { lat: 42.230171, lng: -71.530106 },
  MI: { lat: 43.326618, lng: -84.536095 },
  MN: { lat: 45.694454, lng: -93.900192 },
  MS: { lat: 32.741646, lng: -89.678696 },
  MO: { lat: 38.456085, lng: -92.288368 },
  MT: { lat: 46.921925, lng: -110.454353 },
  NE: { lat: 41.12537, lng: -98.268082 },
  NV: { lat: 38.313515, lng: -117.055374 },
  NH: { lat: 43.452492, lng: -71.563896 },
  NJ: { lat: 40.298904, lng: -74.521011 },
  NM: { lat: 34.840515, lng: -106.248482 },
  NY: { lat: 42.165726, lng: -74.948051 },
  NC: { lat: 35.630066, lng: -79.806419 },
  ND: { lat: 47.528912, lng: -99.784012 },
  OH: { lat: 40.388783, lng: -82.764915 },
  OK: { lat: 35.565342, lng: -96.928917 },
  OR: { lat: 44.572021, lng: -122.070938 },
  PA: { lat: 40.590752, lng: -77.209755 },
  RI: { lat: 41.680893, lng: -71.51178 },
  SC: { lat: 33.856892, lng: -80.945007 },
  SD: { lat: 44.299782, lng: -99.438828 },
  TN: { lat: 35.747845, lng: -86.692345 },
  TX: { lat: 31.054487, lng: -97.563461 },
  UT: { lat: 40.150032, lng: -111.862434 },
  VT: { lat: 44.045876, lng: -72.710686 },
  VA: { lat: 37.769337, lng: -78.169968 },
  WA: { lat: 47.400902, lng: -121.490494 },
  WV: { lat: 38.491226, lng: -80.954453 },
  WI: { lat: 44.268543, lng: -89.616508 },
  WY: { lat: 42.755966, lng: -107.30249 },
};

export function geoCoords(stateCode: string, key: string): { lat: number; lng: number } {
  const base = STATE_GEO[stateCode] ?? { lat: 39.5, lng: -98.35 };
  const h = hash(key);
  // Jitter ±0.5 deg so cities differ from the state centroid.
  const lat = +(base.lat + ((h % 1000) / 1000 - 0.5)).toFixed(4);
  const lng = +(base.lng + (((h >> 5) % 1000) / 1000 - 0.5)).toFixed(4);
  return { lat, lng };
}

export function breadcrumbSchema(
  items: { name: string; url: string }[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
